import { useEffect, useId, useMemo, useRef, useState } from "react";
import { resumirCitas, porcentaje } from "../../utils/resumenCitas";
import "../../styles/CitasResumen.css";

/* ============================================================================
   RESUMEN DEL DÍA · una etiqueta en la barra, el detalle en un popover

   Recepción necesitaba ver cuántas citas del día son particulares y cuántas las
   cubre un tercero, y cómo están repartidos los estados, sin salir de la
   agenda y SIN QUE LA AGENDA PIERDA ALTURA. Por eso esto vive dentro de la
   barra que ya existe y el detalle baja flotando por encima de la cuadrícula.

   NO PIDE NADA AL SERVIDOR: cuenta el mismo arreglo que ya pinta la agenda.

   POR QUÉ UNA ETIQUETA Y NO DOS -- medido, no supuesto. Con la barra real:

     ventana   libre    dos etiquetas completas (374 px)
     1600 px   424 px   cabe
     1440 px   264 px   NO
     1366 px   190 px   NO
     1280 px   104 px   NO

   Y `.cbarra` tiene `flex-wrap: wrap`: pasarse no da scroll, da una segunda
   línea, la barra crece de 58 a ~100 px y la agenda baja. Una sola etiqueta
   (~183 px con las dos micro-barras, ~120 px sin la palabra) sí cabe.

   POR QUÉ EL FILTRO VIVE AQUÍ DENTRO Y NO EN UN CHIP APARTE. Un chip suelto
   pide ~120 px más, que a 1366 no existen. Así que al filtrar la etiqueta SE
   CONVIERTE en el chip removible, con su ✕, y sigue abriendo el popover para
   cambiar de filtro. El filtro por ESTADO no necesita nada: el botón "Filtros"
   ya muestra su contador, que es el mecanismo que ya existía.
   ========================================================================== */

const RETARDO_HOVER = 150; // para que no salte al pasar de largo
const MAX_ASEGURADORAS = 6;

function IconoCalendario() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M8 3v4M16 3v4M3 11h18" />
        </svg>
    );
}

function IconoChevron() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

function IconoCerrar() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
        </svg>
    );
}

/** Micro-barra apilada. Sólo `div` con anchos en %: ni canvas ni librerías. */
function MicroBarra({ partes, titulo, clase = "" }) {
    const total = partes.reduce((t, p) => t + p.n, 0);
    return (
        <span className={`cres__barra ${clase}`} title={titulo} aria-hidden="true">
            {total > 0 &&
                partes
                    .filter((p) => p.n > 0)
                    .map((p) => (
                        <i
                            key={p.clave}
                            className={`cres__seg ${p.clase}`}
                            style={{ width: `${(p.n / total) * 100}%` }}
                        />
                    ))}
        </span>
    );
}

/** Una fila del popover: siempre un botón, nunca un div con onClick. */
function Fila({ clase, etiqueta, n, total, activa, resaltada, onClick, titulo }) {
    return (
        <button
            type="button"
            className={`cres__fila${resaltada ? " cres__fila--resaltada" : ""}${activa ? " cres__fila--activa" : ""}`}
            onClick={onClick}
            title={titulo || etiqueta}
            aria-pressed={activa}
        >
            <span className={`cres__punto ${clase}`} aria-hidden="true" />
            <span className="cres__fila-texto">{etiqueta}</span>
            {total > 0 && <span className="cres__fila-pct">{porcentaje(n, total)}%</span>}
            <span className="cres__fila-num">{n}</span>
        </button>
    );
}

export default function EtiquetaResumen({
    // El resumen del DÍA COMPLETO, ya contado por el backend y con forma dada
    // por `normalizarResumenDia`. `citas` es sólo un camino de respaldo para
    // las pruebas (contar en el front un arreglo de citas).
    resumen = null,
    citas,
    cupos = null,
    etiquetaAlcance = "",
    cargando = false,
    error = false,
    filtroCobertura = null,
    onFiltrarCobertura,
    estadosVisibles = null,
    onCambiarEstados,
}) {
    const [abierto, setAbierto] = useState(false);
    const [fijado, setFijado] = useState(false);
    const [aDerecha, setADerecha] = useState(false);
    const [verTodasAseguradoras, setVerTodasAseguradoras] = useState(false);

    const contenedorRef = useRef(null);
    const disparadorRef = useRef(null);
    const temporizadorRef = useRef(null);
    const idPopover = useId();

    /* El resumen ya viene contado del backend. `citas` es el respaldo para
       las pruebas. Memoizado y ANTES de cualquier `return`: el orden de los
       hooks no puede depender de si está cargando. */
    const r = useMemo(() => resumen ?? resumirCitas(citas), [resumen, citas]);

    const cerrar = (devolverFoco = false) => {
        setAbierto(false);
        setFijado(false);
        setVerTodasAseguradoras(false);
        if (devolverFoco) disparadorRef.current?.focus();
    };

    // Esc cierra y devuelve el foco a la etiqueta.
    useEffect(() => {
        if (!abierto) return undefined;
        const alPulsar = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                cerrar(true);
            }
        };
        const alPulsarFuera = (e) => {
            if (!contenedorRef.current?.contains(e.target)) cerrar(false);
        };
        document.addEventListener("keydown", alPulsar);
        document.addEventListener("mousedown", alPulsarFuera);
        return () => {
            document.removeEventListener("keydown", alPulsar);
            document.removeEventListener("mousedown", alPulsarFuera);
        };
    }, [abierto]);

    useEffect(() => () => clearTimeout(temporizadorRef.current), []);

    /* Si la etiqueta está cerca del borde derecho, el popover se alinea a la
       derecha para no salirse. Se mide al abrir, no en cada render. */
    useEffect(() => {
        if (!abierto || !disparadorRef.current) return;
        const caja = disparadorRef.current.getBoundingClientRect();
        setADerecha(caja.left + 300 > window.innerWidth);
    }, [abierto]);

    const abrirConRetardo = () => {
        clearTimeout(temporizadorRef.current);
        temporizadorRef.current = setTimeout(() => setAbierto(true), RETARDO_HOVER);
    };
    const cerrarSiNoFijado = () => {
        clearTimeout(temporizadorRef.current);
        if (!fijado) setAbierto(false);
    };
    const alternarFijado = () => {
        clearTimeout(temporizadorRef.current);
        if (abierto && fijado) cerrar(false);
        else {
            setAbierto(true);
            setFijado(true);
        }
    };

    // Error al cargar, o el backend no mandó el resumen: nada. Un cero es un
    // dato y aquí no lo sabemos.
    if (error || (!resumen && !citas)) return null;

    /* Cargando: un hueco del mismo tamaño, para que la barra no salte de ancho
       ni la agenda se mueva mientras llega la respuesta. */
    if (cargando) {
        return (
            <>
                <span className="cbarra__separador cres__separador" aria-hidden="true" />
                <span className="cres cres--esqueleto" aria-hidden="true" />
            </>
        );
    }

    const hayCitas = r.total > 0;

    const partesCobertura = [
        { clave: "particular", clase: "cres__c--particular", n: r.particular },
        { clave: "cubierto", clase: "cres__c--cubierto", n: r.cubierto },
    ];
    const partesEstados = r.estados
        .filter((e) => e.n > 0)
        .map((e) => ({ clave: e.clave, clase: `cagenda__cita--${e.clave}`, n: e.n }));

    /* Sólo se listan los estados que EXISTEN ese día. En la base ningún día
       tiene más de 3 estados distintos, así que pintar los siete sería casi
       siempre cuatro filas en cero. La leyenda del pie sigue mostrando los
       siete colores. */
    const estadosConCitas = r.estados.filter((e) => e.n > 0);

    const aseguradorasVisibles = verTodasAseguradoras
        ? r.aseguradoras
        : r.aseguradoras.slice(0, MAX_ASEGURADORAS);
    const ocultas = r.aseguradoras.length - aseguradorasVisibles.length;

    const filtrando = Boolean(filtroCobertura?.tipo);
    const etiquetaFiltro = filtrando
        ? filtroCobertura.tipo === "aseguradora"
            ? filtroCobertura.valor
            : filtroCobertura.tipo === "particular"
            ? "Particular"
            : "Cubierto"
        : "";
    const claseFiltro =
        filtroCobertura?.tipo === "particular" ? "cres__c--particular" : "cres__c--cubierto";
    const nFiltro = filtrando
        ? filtroCobertura.tipo === "particular"
            ? r.particular
            : filtroCobertura.tipo === "cubierto"
            ? r.cubierto
            : r.aseguradoras.find((a) => a.nombre === filtroCobertura.valor)?.n ?? 0
        : 0;

    const titulo = etiquetaAlcance || "Resumen del día";

    const alternarCobertura = (filtro) => {
        const mismo =
            filtroCobertura?.tipo === filtro.tipo && filtroCobertura?.valor === filtro.valor;
        onFiltrarCobertura?.(mismo ? null : filtro);
    };

    const alternarEstado = (id) => {
        const solo = estadosVisibles?.length === 1 && estadosVisibles[0] === id;
        onCambiarEstados?.(solo ? null : [id]);
    };

    return (
        <>
            <span className="cbarra__separador cres__separador" aria-hidden="true" />

            <span
                className={`cres${abierto ? " cres--abierto" : ""}${filtrando ? " cres--filtrando" : ""}`}
                ref={contenedorRef}
                onMouseEnter={abrirConRetardo}
                onMouseLeave={cerrarSiNoFijado}
            >
                <button
                    type="button"
                    ref={disparadorRef}
                    className="cres__cuerpo"
                    onClick={alternarFijado}
                    aria-expanded={abierto}
                    aria-haspopup="dialog"
                    aria-controls={abierto ? idPopover : undefined}
                    title={titulo}
                >
                    {filtrando ? (
                        <>
                            <span className={`cres__punto ${claseFiltro}`} aria-hidden="true" />
                            <span className="cres__palabra">{etiquetaFiltro}</span>
                            <b className="cres__num">{nFiltro}</b>
                        </>
                    ) : (
                        <>
                            <span className="cres__icono" aria-hidden="true">
                                <IconoCalendario />
                            </span>
                            <b className="cres__num">{r.total}</b>
                            <span className="cres__palabra">
                                {r.total === 1 ? "cita" : "citas"}
                            </span>
                            <MicroBarra
                                partes={partesCobertura}
                                clase="cres__barra--cobertura"
                                titulo={`Particular ${r.particular} · Cubierto ${r.cubierto}`}
                            />
                            <MicroBarra
                                partes={partesEstados}
                                clase="cres__barra--estados"
                                titulo={estadosConCitas.map((e) => `${e.etiqueta} ${e.n}`).join(" · ")}
                            />
                        </>
                    )}
                    <span className="cres__chevron" aria-hidden="true">
                        <IconoChevron />
                    </span>
                </button>

                {/* El ✕ va fuera del botón: un botón dentro de otro no es HTML
                    válido. Mismo patrón que el chip de especialidad. */}
                {filtrando && (
                    <button
                        type="button"
                        className="cres__quitar"
                        onClick={() => onFiltrarCobertura?.(null)}
                        aria-label={`Quitar el filtro ${etiquetaFiltro}`}
                    >
                        <IconoCerrar />
                    </button>
                )}

                {abierto && (
                    <div
                        className={`cres__popover${aDerecha ? " cres__popover--derecha" : ""}`}
                        id={idPopover}
                        role="dialog"
                        aria-label={titulo}
                    >
                        {!hayCitas ? (
                            <p className="cres__vacio">Sin citas este día.</p>
                        ) : (
                            <>
                                <div className="cres__seccion">
                                    <div className="cres__cabecera">
                                        <span className="cres__titulo">Cobertura del día</span>
                                        <span className="cres__total">{r.total} citas</span>
                                    </div>
                                    <MicroBarra
                                        partes={partesCobertura}
                                        clase="cres__barra--ancha"
                                        titulo=""
                                    />
                                    <Fila
                                        clase="cres__c--particular"
                                        etiqueta="Particular"
                                        n={r.particular}
                                        total={r.total}
                                        activa={filtroCobertura?.tipo === "particular"}
                                        onClick={() => alternarCobertura({ tipo: "particular" })}
                                    />
                                    <Fila
                                        clase="cres__c--cubierto"
                                        etiqueta="Cubierto"
                                        n={r.cubierto}
                                        total={r.total}
                                        activa={filtroCobertura?.tipo === "cubierto"}
                                        titulo="Seguros, empresas y convenios"
                                        onClick={() => alternarCobertura({ tipo: "cubierto" })}
                                    />

                                    {/* Si esto aparece, las partes no suman el
                                        total: se dice, no se esconde. */}
                                    {r.sinCobertura > 0 && (
                                        <p className="cres__aviso">
                                            {r.sinCobertura} sin cobertura registrada
                                        </p>
                                    )}

                                    {r.aseguradoras.length > 0 && (
                                        <>
                                            <p className="cres__subtitulo">Por aseguradora</p>
                                            {aseguradorasVisibles.map((a) => (
                                                <button
                                                    key={a.nombre}
                                                    type="button"
                                                    className={`cres__fila cres__fila--simple${
                                                        filtroCobertura?.tipo === "aseguradora" &&
                                                        filtroCobertura?.valor === a.nombre
                                                            ? " cres__fila--activa"
                                                            : ""
                                                    }`}
                                                    title={a.nombre}
                                                    onClick={() =>
                                                        alternarCobertura({
                                                            tipo: "aseguradora",
                                                            valor: a.nombre,
                                                        })
                                                    }
                                                >
                                                    <span className="cres__fila-texto">{a.nombre}</span>
                                                    <span className="cres__fila-num">{a.n}</span>
                                                </button>
                                            ))}
                                            {ocultas > 0 && (
                                                <button
                                                    type="button"
                                                    className="cres__mas"
                                                    onClick={() => setVerTodasAseguradoras(true)}
                                                >
                                                    +{ocultas} aseguradoras
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="cres__seccion">
                                    <div className="cres__cabecera">
                                        <span className="cres__titulo">Estado de las citas</span>
                                    </div>
                                    <MicroBarra
                                        partes={partesEstados}
                                        clase="cres__barra--ancha"
                                        titulo=""
                                    />
                                    {estadosConCitas.map((e) => (
                                        <Fila
                                            key={e.id}
                                            clase={`cagenda__cita--${e.clave}`}
                                            etiqueta={e.etiqueta}
                                            n={e.n}
                                            total={0}
                                            /* Ámbar = pendiente. Sólo se
                                               resalta lo que pide gestión;
                                               cuando el destacado es el
                                               Confirmada de relleno no hay
                                               nada pendiente que marcar. */
                                            resaltada={
                                                r.destacado?.id === e.id &&
                                                (e.id === 1 || e.id === 4)
                                            }
                                            activa={
                                                estadosVisibles?.length === 1 &&
                                                estadosVisibles[0] === e.id
                                            }
                                            onClick={() => alternarEstado(e.id)}
                                        />
                                    ))}
                                </div>

                                {/* Los cupos salen de la MISMA cuenta que ya
                                    muestra cada médico ("4/13"); si no se puede
                                    calcular, la línea no se pinta. */}
                                {cupos && (
                                    <p className="cres__pie">
                                        Ocupación: {cupos.ocupados} de {cupos.totales} cupos ·{" "}
                                        {cupos.libres} libres
                                    </p>
                                )}
                                <p className="cres__ayuda">Clic en una fila para filtrar la agenda.</p>
                            </>
                        )}
                    </div>
                )}
            </span>
        </>
    );
}
